import { Component, OnInit, OnDestroy, ViewChild, ElementRef, effect } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DashboardFacade } from '../../../facade/dashboard.facade';
import { EditDashboardIdComponent } from '../edit-dashboard-id/edit-dashboard-id.component';
import { NbDialogRef, NB_DIALOG_CONFIG } from '@nebular/theme';
import { PermissionsService } from '../../../../shared/components/permissions/services/permissions.service';
import { AssetsRequest } from '../../../../main/modules/asset/models/assets-request.model';
import { Asset } from '../../../../main/modules/asset/models/asset.model';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'morva-import-dashboard-datasources-modal',
  templateUrl: './import-dashboard-datasources-modal.component.html',
  styleUrl: './import-dashboard-datasources-modal.component.scss'
})
export class ImportDashboardDatasourcesModalComponent implements OnInit, OnDestroy {
  form: FormGroup;
  formPristineAfterLoad = true;
  loading = false;
  dashboardName: any;
  dashboardId: any;
  workspaceId: any;
  showWarning = false;
  data: any;

  // Datasource related properties
  datasourceList: Asset[] = [];
  showDatasourceList: Asset[] = [];
  currentPage = 1;
  pageSize = 200;
  totalPages = 1;
  searchKey = '';
  isLoadingDatasources = false;
  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();
  currentDatasourceIndex = 0;
  totalDatasources = 0;
  hasMoreData = true;
  selectedDataSource: string;
  tempSelectedDatasourceName: string | null = null;
  tempSelectedDatasourceId: string | null = null;
  isErrorExpanded = false

  @ViewChild('searchDatasourceKey') searchDatasourceInput: ElementRef;
  errors: string[];
  get allDatasourcesSelected(): boolean {
    return this.dashboardFacade.state().importDatasources.every(ds => ds.status === true);
  }
  constructor(
    private fb: FormBuilder,
    private dialogRef: NbDialogRef<EditDashboardIdComponent>,
    private permissionsService: PermissionsService,
    public dashboardFacade: DashboardFacade,
  ) {
    this.initForm();
    let prevStepIndex = this.dashboardFacade.state().importStepIndex;

    effect(() => {
      const state = this.dashboardFacade.state();
      const currentStepIndex = state.importStepIndex;
    
      if (currentStepIndex > prevStepIndex) {
        this.resetTempSelection();
      }
      if (currentStepIndex < prevStepIndex) {
        this.restoreTempSelection();
      }
    
      prevStepIndex = currentStepIndex;
    });
    
  }

  ngOnInit(): void {
    (document.activeElement as HTMLElement)?.blur();
    this.form.markAsPristine();
    this.form.markAsUntouched();

    // 1. Search with debounce, distinctUntilChanged, and cancel previous requests
    this.searchSubject$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(searchTerm => {

        this.searchKey = searchTerm?.trim() || '';
        this.currentPage = 1;
        this.hasMoreData = true;
        this.isLoadingDatasources = true;

        const request: AssetsRequest = {
          workspaceId: this.workspaceId,
          searchKey: this.searchKey || undefined,
          pageNumber: 1,
          pageSize: this.pageSize,
          needCount: true,
          ascending: true,
        };
        return this.permissionsService
          .loadDataSourceAssets$(request)
          .pipe(catchError(() => of({ assets: [], totalPages: 1 })));
      }),
      takeUntil(this.destroy$)
    ).subscribe(response => {
      const assets = response.assets ?? [];
      this.datasourceList = assets;
      this.showDatasourceList = [...assets];
      this.totalPages = response.totalPages ?? 1;
      this.totalDatasources = assets.length;
      this.hasMoreData = this.currentPage < this.totalPages;
      this.isLoadingDatasources = false;
    });

    // 2. Load initial datasources if workspaceId is available
    if (this.workspaceId) {
      this.loadDatasources();
    }

    // 3. Track form changes
    this.form.valueChanges.subscribe(() => {
      if (!this.formPristineAfterLoad) return;
      this.formPristineAfterLoad = false;
    });

    // 4. Set initial selection from facade
    const current = this.dashboardFacade.state().importCurrentDatasource;
    this.form.get('datasourcesItems')?.setValue(current?.selectedDatasourceId ?? null);
    this.tempSelectedDatasourceId = current?.selectedDatasourceId ?? null;
    this.tempSelectedDatasourceName = current?.selectedDataSourceName ?? null;
  }

  private restoreTempSelection(): void {
    const current = this.dashboardFacade.state().importCurrentDatasource;
  
    this.tempSelectedDatasourceId = current?.selectedDatasourceId ?? null;
    this.tempSelectedDatasourceName = current?.selectedDataSourceName ?? null;
  
    this.form.get('datasourcesItems')?.setValue(this.tempSelectedDatasourceId);
  }
  

  private resetTempSelection(): void {
    this.form.get('datasourcesItems')?.setValue(null);
    this.tempSelectedDatasourceId = null;
    this.tempSelectedDatasourceName = null;
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resetTempSelection();
  }

  private initForm(): void {
    this.form = this.fb.group({
      datasourcesItems: [null, Validators.required],
    });
  }

  loadDatasources(): void {
    if (!this.workspaceId) {
      return;
    }

    if (this.isLoadingDatasources || (this.currentPage > this.totalPages && this.totalPages > 0)) {
      this.hasMoreData = false;
      return;
    }

    this.isLoadingDatasources = true;

    const request: AssetsRequest = {
      workspaceId: this.workspaceId,
      searchKey: this.searchKey || undefined,
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      needCount: true,
      ascending: true,
    };

    this.permissionsService.loadDataSourceAssets$(request).subscribe(
      response => {
        const assets = response.assets ?? [];

        if (this.currentPage === 1) {
          this.datasourceList = assets;
          this.showDatasourceList = [...assets];
        } else {
          this.datasourceList = [...this.datasourceList, ...assets];
          this.showDatasourceList = [...this.datasourceList];
        }

        this.totalPages = response.totalPages ?? 1;
        this.totalDatasources = this.datasourceList.length;
        this.hasMoreData = this.currentPage < this.totalPages;

        this.isLoadingDatasources = false;
      },
      error => {
        this.isLoadingDatasources = false;
        this.hasMoreData = false;
      }
    );
  }

  searchDatasource(value: string): void {
 
    this.searchSubject$.next(value);
  }

  selectDatasourceItem(assetId: string): void {
    this.form.get('datasourcesItems')?.setValue(assetId);

    const selected = this.datasourceList.find(ds => ds.assetId === assetId);

    this.tempSelectedDatasourceId = assetId;
    this.tempSelectedDatasourceName = selected?.assetName ?? null;

    this.dashboardFacade.changeImportDatasourceSelection(
      this.tempSelectedDatasourceId,
      this.tempSelectedDatasourceName
    );
  }


  clearSelection(event?: Event): void {
    event?.stopPropagation();

    this.form.get('datasourcesItems')?.setValue(null);

    this.tempSelectedDatasourceId = null;
    this.tempSelectedDatasourceName = null;

    this.dashboardFacade.changeImportDatasourceSelection(null, null);
  }


  getSelectedDatasourceName(): string {
    const selectedValue = this.form.get('datasourcesItems')?.value;
    if (selectedValue && this.datasourceList.length > 0) {
      const selected = this.datasourceList.find(ds => ds.assetId === selectedValue);
      return selected?.assetName || '';
    }
    return '';
  }

  resetShowDatasources(): void {
    if (this.searchDatasourceInput) {
      this.searchDatasourceInput.nativeElement.value = '';
    }
      this.searchKey = '';
      this.currentPage = 1;
      this.datasourceList = [];
      this.showDatasourceList = [];
      this.hasMoreData = true;
      if (this.workspaceId) {
        this.loadDatasources();
      }
    }

  toggleErrorBox() {
    this.isErrorExpanded = !this.isErrorExpanded
  }

  onMenuScroll(event: any): void {
    const element = event.target;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight * 0.8 &&
      this.hasMoreData &&
      !this.isLoadingDatasources &&
      this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadDatasources();
    }
  }
  onNext(): void {
    if (!this.tempSelectedDatasourceId) {
      this.errors = ['لطفاً یک منبع داده انتخاب کنید.'];
      return;
    }

    this.loading = true;

    this.dashboardFacade.checkImportDatasource(
      this.workspaceId,
      this.tempSelectedDatasourceId,
      this.tempSelectedDatasourceName
    );
  }

  onConfirm(): void {
    if ((this.dashboardFacade.state().importIsLastStep  )) {
      this.onFinalize();
    } else {
      this.onNext();
    }
  }
  
  private onFinalize(): void {
    this.dashboardFacade.finalizeImportDashboard(this.workspaceId);
  }
  
  closeDialog(): void {
    this.dialogRef.close(false);
  }
}